import { create } from 'zustand';

/**
 * Search Navigation Store
 *
 * This store manages keyboard navigation for search results using a pattern
 * similar to cmdk (https://github.com/pacocoursey/cmdk).
 *
 * Inspired by https://github.com/dip/cmdk/blob/main/cmdk/src/index.tsx
 *
 * WHY ZUSTAND + DIRECT DOM MANIPULATION?
 *
 * Problem: React's state-based rendering is too slow for smooth keyboard navigation
 * when holding down arrow keys. Each setState triggers a render cycle, causing
 * visible lag between keypress and visual feedback.
 *
 * Solution: Use Zustand with direct DOM manipulation for instant visual updates:
 *
 * 1. **Zustand over useState**: External store that doesn't force re-renders on
 *    every state change. Only components that subscribe to specific state slices
 *    re-render.
 *
 * 2. **Direct DOM manipulation**: Update aria-selected attributes and scroll
 *    position synchronously BEFORE updating Zustand state. This provides instant
 *    visual feedback while React updates happen asynchronously in the background.
 *
 * 3. **Similar to cmdk architecture**: They use useSyncExternalStore with direct
 *    DOM queries and updates. Our approach is simpler using Zustand but achieves
 *    the same performance benefit.
 *
 * Flow when user presses arrow key:
 * 1. Handler calls moveNext()/movePrev()
 * 2. setActiveIndex() immediately updates DOM (aria-selected, scroll)
 * 3. Then updates Zustand state (triggers minimal re-renders)
 * 4. User sees instant feedback, no lag
 */

export const RESULT_LIST_ID = 'search-results-listbox';
export const INPUT_ID = 'global-search-input';
export const RESULT_ITEM_ID_PREFIX = 'search-result-';

interface SearchNavigationState {
  activeIndex: number;
  totalResults: number;
  setActiveIndex: (index: number) => void;
  setTotalResults: (total: number) => void;
  moveNext: () => void;
  movePrev: () => void;
  moveToIndex: (index: number) => void;
  reset: () => void;
}

export const useSearchNavigationStore = create<SearchNavigationState>(
  (set, get) => ({
    activeIndex: -1,
    totalResults: 0,

    setActiveIndex: (index: number) => {
      const { activeIndex: oldIndex } = get();

      // DIRECT DOM MANIPULATION FOR INSTANT VISUAL FEEDBACK
      // This happens synchronously before state update, avoiding React render lag

      // Remove aria-selected from previously active item
      if (oldIndex >= 0) {
        const oldElement = document.getElementById(
          `${RESULT_ITEM_ID_PREFIX}${oldIndex}`,
        );
        if (oldElement) {
          oldElement.setAttribute('aria-selected', 'false');
        }
      }

      if (index >= 0) {
        // Add aria-selected to new active item
        const newElement = document.getElementById(`search-result-${index}`);
        if (newElement) {
          newElement.setAttribute('aria-selected', 'true');

          // If navigating to index 0 (first item), scroll results list to top
          if (index === 0) {
            const resultsList = document.getElementById(RESULT_LIST_ID);
            if (resultsList) {
              resultsList.scrollTop = 0;
            }
          } else {
            // Otherwise scroll the item itself into view
            newElement.scrollIntoView({
              block: 'nearest',
              behavior: 'auto',
            });
          }
        }

        // Update input's aria-activedescendant for accessibility
        const input = document.getElementById(INPUT_ID);
        if (input) {
          input.setAttribute(
            'aria-activedescendant',
            `${RESULT_ITEM_ID_PREFIX}${index}`,
          );
        }
      } else {
        // No active item, remove aria-activedescendant
        const input = document.getElementById(INPUT_ID);
        if (input) {
          input.removeAttribute('aria-activedescendant');
        }
      }

      // Update Zustand state last (triggers minimal re-renders)
      set({ activeIndex: index });
    },

    setTotalResults: (total: number) => {
      set({ totalResults: total });
    },

    moveNext: () => {
      const { activeIndex, totalResults, setActiveIndex } = get();
      if (totalResults === 0) return;
      const nextIndex = activeIndex < totalResults - 1 ? activeIndex + 1 : 0;
      setActiveIndex(nextIndex);
    },

    movePrev: () => {
      const { activeIndex, totalResults, setActiveIndex } = get();
      if (totalResults === 0) return;
      const prevIndex = activeIndex > 0 ? activeIndex - 1 : totalResults - 1;
      setActiveIndex(prevIndex);
    },

    moveToIndex: (index: number) => {
      const { setActiveIndex } = get();
      setActiveIndex(index);
    },

    reset: () => {
      const { setActiveIndex } = get();
      setActiveIndex(-1);
    },
  }),
);
